import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import '../index.css';

function DashboardPage() {
    const { hasRole } = useAuth();
    const [metrics, setMetrics] = useState({
        invoices: {},
        products: {},
        users: {},
        workflow: {}
    });
    const [loading, setLoading] = useState(true);
    const [exportLoading, setExportLoading] = useState({}); // Track loading per export type

    useEffect(() => {
        if (hasRole('admin')) {
            loadMetrics();
        }
    }, []);

    const loadMetrics = async () => {
        try {
            setLoading(true);
            const [invoiceData, productData, userData, workflowData] = await Promise.all([
                axios.get('http://localhost:5000/api/metrics/invoices'),
                axios.get('http://localhost:5000/api/metrics/products'),
                axios.get('http://localhost:5000/api/metrics/users'),
                axios.get('http://localhost:5000/api/metrics/workflow')
            ]);

            setMetrics({
                invoices: invoiceData.data,
                products: productData.data,
                users: userData.data,
                workflow: workflowData.data
            });
        } catch (err) {
            console.error('Error loading metrics:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async (type) => {
        try {
            setExportLoading(prev => ({ ...prev, [type]: true }));
            const response = await axios.get(`http://localhost:5000/api/export/${type}`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${type}-${Date.now()}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            alert('Export failed: ' + (err.response?.data?.error || err.message));
        } finally {
            setExportLoading(prev => ({ ...prev, [type]: false }));
        }
    };

    if (!hasRole('admin')) {
        return (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
                <h2>Access Denied</h2>
                <p>Only administrators can access this page.</p>
            </div>
        );
    }

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '3rem' }}>Loading dashboard...</div>;
    }

    return (
        <div>
            <h1 className="page-title">📊 Admin Dashboard</h1>

            {/* Sales Order Metrics */}
            <div className="card" style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h2>📋 Sales Order Metrics</h2>
                    <button
                        onClick={() => handleExport('invoices')}
                        disabled={exportLoading['invoices']}
                        className={`btn-secondary ${exportLoading['invoices'] ? 'btn-loading' : ''}`}
                        style={{
                            padding: '0.6rem 1.2rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            opacity: exportLoading['invoices'] ? 0.7 : 1,
                            cursor: exportLoading['invoices'] ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {exportLoading['invoices'] ? (
                            <><div className="loading-spinner spinner-small" style={{ borderTopColor: '#344ea1' }}></div> Exporting...</>
                        ) : (
                            <>📥 Export Sales Orders</>
                        )}
                    </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                    <div style={{ padding: '1.5rem', background: '#eff6ff', borderRadius: '12px' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e40af' }}>
                            {metrics.invoices.totalInvoices || 0}
                        </div>
                        <div style={{ color: '#64748b', marginTop: '0.5rem' }}>Total Sales Orders</div>
                    </div>
                    <div style={{ padding: '1.5rem', background: '#dcfce7', borderRadius: '12px' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#15803d' }}>
                            {metrics.invoices.completedInvoices || 0}
                        </div>
                        <div style={{ color: '#64748b', marginTop: '0.5rem' }}>Completed</div>
                    </div>
                    <div style={{ padding: '1.5rem', background: '#fef3c7', borderRadius: '12px' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#a16207' }}>
                            {metrics.invoices.pendingInvoices || 0}
                        </div>
                        <div style={{ color: '#64748b', marginTop: '0.5rem' }}>Pending</div>
                    </div>
                    <div style={{ padding: '1.5rem', background: '#f3e8ff', borderRadius: '12px' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#7c3aed' }}>
                            ${(metrics.invoices.totalAmount || 0).toLocaleString()}
                        </div>
                        <div style={{ color: '#64748b', marginTop: '0.5rem' }}>Total Amount</div>
                    </div>
                </div>
            </div>

            {/* Workflow Status */}
            <div className="card" style={{ marginBottom: '2rem' }}>
                <h2 style={{ marginBottom: '1.5rem' }}>🔄 Workflow Status</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                    <div style={{ padding: '1rem', background: '#fef3c7', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                            {metrics.workflow.draft || 0}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#64748b' }}>Draft</div>
                    </div>
                    <div style={{ padding: '1rem', background: '#ddd6fe', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                            {metrics.workflow['pending-review'] || 0}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#64748b' }}>Pending Review</div>
                    </div>
                    <div style={{ padding: '1rem', background: '#d1fae5', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                            {metrics.workflow.completed || 0}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#64748b' }}>Completed</div>
                    </div>
                </div>
            </div>

            {/* Product Metrics */}
            <div className="card" style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h2>📦 Product Metrics</h2>
                    <button
                        onClick={() => handleExport('products')}
                        disabled={exportLoading['products']}
                        className={`btn-secondary ${exportLoading['products'] ? 'btn-loading' : ''}`}
                        style={{
                            padding: '0.6rem 1.2rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            opacity: exportLoading['products'] ? 0.7 : 1,
                            cursor: exportLoading['products'] ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {exportLoading['products'] ? (
                            <><div className="loading-spinner spinner-small" style={{ borderTopColor: '#344ea1' }}></div> Exporting...</>
                        ) : (
                            <>📥 Export Products</>
                        )}
                    </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
                    <div style={{ padding: '1.2rem', background: '#e0e7ff', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{metrics.products.totalProducts || 0}</div>
                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Total Products</div>
                    </div>
                    <div style={{ padding: '1.2rem', background: '#d1fae5', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{metrics.products.activeProducts || 0}</div>
                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Active</div>
                    </div>
                    <div style={{ padding: '1.2rem', background: '#fed7aa', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{metrics.products.websiteProducts || 0}</div>
                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Website</div>
                    </div>
                    <div style={{ padding: '1.2rem', background: '#fbcfe8', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{metrics.products.customProducts || 0}</div>
                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Custom</div>
                    </div>
                </div>
            </div>

            {/* User Metrics */}
            <div className="card">
                <h2 style={{ marginBottom: '1.5rem' }}>👥 User Metrics</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem' }}>
                    <div style={{ padding: '1rem', background: '#e0e7ff', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{metrics.users.totalUsers || 0}</div>
                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Total Users</div>
                    </div>
                    <div style={{ padding: '1rem', background: '#d1fae5', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{metrics.users.activeUsers || 0}</div>
                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Active</div>
                    </div>
                    <div style={{ padding: '1rem', background: '#fef3c7', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{metrics.users.byRole?.admin || 0}</div>
                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Admins</div>
                    </div>
                    <div style={{ padding: '1rem', background: '#ddd6fe', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{metrics.users.byRole?.['invoice-editor'] || 0}</div>
                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>IE</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DashboardPage;
